import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

import { audioDB } from '@/lib/storage/audioDB';
import { getPreferredMimeType } from '@/lib/utils/audioFormats';
import { useEncounterSessionStore } from '@/lib/stores/encounter-session-store';

interface EncounterSessionContextValue {
  cancelRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  startRecording: () => Promise<void>;
  stopRecording: () => Promise<{ blob: Blob; duration: number } | void>;
}

const EncounterSessionContext = createContext<EncounterSessionContextValue | null>(null);

export function useEncounterSession() {
  const ctx = useContext(EncounterSessionContext);
  if (!ctx) {
    throw new Error('useEncounterSession must be used within EncounterSessionProvider');
  }
  return ctx;
}

export function EncounterSessionProvider({ children }: { children: ReactNode }) {
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const pauseStartRef = useRef<number>(0);

  const { recordingMeta, setRecordingMeta } = useEncounterSessionStore();

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const startRecording = useCallback(async () => {
    const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    const mimeType = getPreferredMimeType();
    if (!mimeType) {
      stream.getTracks().forEach((t) => t.stop());
      throw new Error('No supported audio format found in your browser');
    }

    const mediaRecorder = new MediaRecorder(stream, { mimeType });
    chunksRef.current = [];

    mediaRecorder.ondataavailable = (event) => {
      if (event.data.size > 0) {
        chunksRef.current.push(event.data);
      }
    };

    mediaRecorder.start(100);
    mediaRecorderRef.current = mediaRecorder;
    streamRef.current = stream;

    setRecordingMeta({
      status: 'recording',
      startedAt: Date.now(),
      pausedDuration: 0,
      finalDuration: undefined,
    });
  }, [setRecordingMeta]);

  const pauseRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'recording') {
      mr.pause();
      pauseStartRef.current = Date.now();
      setRecordingMeta({ status: 'paused' });
    }
  }, [setRecordingMeta]);

  const resumeRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state === 'paused') {
      mr.resume();
      const additionalPause = pauseStartRef.current > 0 ? Date.now() - pauseStartRef.current : 0;
      pauseStartRef.current = 0;
      const store = useEncounterSessionStore.getState();
      setRecordingMeta({
        status: 'recording',
        pausedDuration: store.recordingMeta.pausedDuration + additionalPause,
      });
    }
  }, [setRecordingMeta]);

  const stopRecording = useCallback(async () => {
    const mr = mediaRecorderRef.current;
    if (!mr) return;

    return new Promise<{ blob: Blob; duration: number } | void>((resolve) => {
      mr.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType });
        const store = useEncounterSessionStore.getState();
        const { startedAt, pausedDuration } = store.recordingMeta;

        let totalPaused = pausedDuration;
        if (pauseStartRef.current > 0) {
          totalPaused += Date.now() - pauseStartRef.current;
          pauseStartRef.current = 0;
        }

        const finalDuration = startedAt ? Date.now() - startedAt - totalPaused : 0;

        await audioDB.save({ blob, duration: finalDuration });

        streamRef.current?.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
        mediaRecorderRef.current = null;
        chunksRef.current = [];

        setRecordingMeta({
          status: 'stopped',
          finalDuration,
          pausedDuration: totalPaused,
        });

        resolve({ blob, duration: finalDuration });
      };

      mr.stop();
    });
  }, [setRecordingMeta]);

  const cancelRecording = useCallback(() => {
    const mr = mediaRecorderRef.current;
    if (mr && mr.state !== 'inactive') {
      mr.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
    chunksRef.current = [];
    pauseStartRef.current = 0;
    clearTimer();

    setRecordingMeta({
      status: 'idle',
      startedAt: null,
      pausedDuration: 0,
      finalDuration: undefined,
    });
  }, [setRecordingMeta, clearTimer]);

  useEffect(() => {
    return () => {
      streamRef.current?.getTracks().forEach((t) => t.stop());
      clearTimer();
    };
  }, [clearTimer]);

  return (
    <EncounterSessionContext.Provider
      value={{
        startRecording,
        pauseRecording,
        resumeRecording,
        stopRecording,
        cancelRecording,
      }}
    >
      {children}
    </EncounterSessionContext.Provider>
  );
}
